import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import TreeView from "../components/TreeView";
import TreeTable from "../components/TreeTable";
import {
    Network, Scale, ArrowLeft, ArrowRight, Search, Copy,
    GitBranch, Table2, Plus, X, CheckCircle2, AlertTriangle,
    Crown, TrendingUp, ChevronDown
} from "lucide-react";

function gcd(a, b) { return b === 0 ? a : gcd(b, a % b); }

export default function Tree() {
    const { user }  = useAuth();
    const navigate  = useNavigate();

    const [root,        setRoot]       = useState(null);
    const [depth,       setDepth]      = useState(10);
    const [loading,     setLoading]    = useState(false);
    const [teamMembers, setTeamMembers]= useState([]);
    const [selectedId,  setSelectedId] = useState(null);
    const [viewMode,    setViewMode]   = useState("table");
    const [search,      setSearch]     = useState("");
    const [wingFilter,  setWingFilter] = useState("ALL");
    const [copied,      setCopied]     = useState(false);

    const isAdmin = user?.role === "admin";

    useEffect(() => {
        if (!user) return;
        if (isAdmin) {
            // Admin sees all members in the system
            api.get("/admin/bonus-overview")
                .then(r => setTeamMembers(r.data))
                .catch(() => {});
        } else {
            // Members see everyone in their own downline (all depths)
            api.get("/users/my-team")
                .then(r => setTeamMembers(r.data))
                .catch(() => {});
        }
    }, [user, isAdmin]);

    useEffect(() => {
        if (user && !selectedId) setSelectedId(user.id);
    }, [user, selectedId]);

    useEffect(() => {
        if (!selectedId) return;
        setLoading(true);
        setRoot(null);
        const url = selectedId === user?.id
            ? "/users/tree"
            : isAdmin
                ? `/admin/users/${selectedId}/tree`
                : `/users/referral/${selectedId}/tree`;
        api.get(url, { params: { depth } })
            .then(r  => setRoot(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [selectedId, depth, isAdmin, user?.id]);

    const leftMembers  = teamMembers.filter(m => m.side === "L");
    const rightMembers = teamMembers.filter(m => m.side === "R");
    const isOwn        = selectedId === user?.id;
    const selectedMember = teamMembers.find(m => m.id === selectedId);

    const stats = useMemo(() => {
        if (!root) return null;
        const left  = root.left_active_count  || 0;
        const right = root.right_active_count || 0;
        const total = left + right;
        const leftPct  = total > 0 ? Math.round((left  / total) * 100) : 0;
        const g        = left && right ? gcd(left, right) : 1;
        const ratio    = total === 0 ? "—" : `${Math.round(left / g)}:${Math.round(right / g)}`;
        const balanced = left > 0 && right > 0 && Math.max(left, right) / Math.min(left, right) < 2;
        return { left, right, total, leftPct, ratio, balanced };
    }, [root]);

    const copyId = () => {
        if (!user?.referral_code) return;
        navigator.clipboard.writeText(user.referral_code).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const WING_PILLS = [
        { label: "All Wings", value: "ALL"      },
        { label: "Direct",    value: "DIRECT"   },
        { label: "Left",      value: "L"        },
        { label: "Right",     value: "R"        },
        { label: "Sovereign", value: "SOVEREIGN"},
    ];

    return (
        <AppShell>
            <div className="space-y-5 pb-12">

                {/* ── Header ── */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div>
                        <div className="overline">My Network</div>
                        <h1 className="text-2xl font-extrabold text-[#0C2B5C] tracking-tight mt-0.5 flex items-center gap-2.5 flex-wrap">
                            Binary Network Tree
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
                                bg-teal-50 text-teal-700 border border-teal-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
                                Live Tree Sync
                            </span>
                        </h1>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search tree members..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="pl-9 pr-4 py-2 bg-white rounded-xl text-xs shadow-sm border border-slate-100
                                    outline-none focus:ring-2 focus:ring-[#2172E5]/20 focus:border-[#2172E5]/40 transition-all w-52"
                            />
                        </div>

                        {/* Sponsor ID chip */}
                        {user?.referral_code && (
                            <div className="flex items-center gap-2 bg-gradient-to-r from-teal-50 to-emerald-50
                                border border-emerald-100 px-3.5 py-2 rounded-xl">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-teal-800">
                                    Sponsor ID
                                </span>
                                <span className="font-mono text-sm font-bold text-emerald-800 tracking-wide">
                                    {user.referral_code}
                                </span>
                                <button onClick={copyId} className="text-teal-600 hover:text-teal-800 transition-colors">
                                    <Copy className="w-3 h-3" />
                                </button>
                                {copied && (
                                    <span className="text-[10px] text-teal-600 font-bold">Copied!</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Stats bar ── */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Total Network Power */}
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm
                            flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    Total Network Power
                                </p>
                                <h3 className="text-2xl font-bold text-slate-800">
                                    {stats.total}{" "}
                                    <span className="text-base font-semibold text-slate-500">Members</span>
                                </h3>
                                <p className="text-[11px] text-teal-600 font-semibold flex items-center gap-1">
                                    <TrendingUp className="w-3 h-3" /> Active network
                                </p>
                            </div>
                            <div className="w-11 h-11 bg-indigo-50 rounded-xl grid place-items-center text-indigo-500 shrink-0">
                                <Network className="w-5 h-5" />
                            </div>
                        </div>

                        {/* Left Wing */}
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm
                            flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    Left Wing Pool
                                </p>
                                <h3 className="text-2xl font-bold text-teal-600">
                                    {stats.left}{" "}
                                    <span className="text-base font-semibold text-teal-500">Members</span>
                                </h3>
                                <p className="text-[11px] text-teal-600 font-semibold flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" /> {stats.leftPct}% network share
                                </p>
                            </div>
                            <div className="w-11 h-11 bg-teal-50 rounded-xl grid place-items-center text-teal-500 shrink-0">
                                <ArrowLeft className="w-5 h-5" />
                            </div>
                        </div>

                        {/* Right Wing */}
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm
                            flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    Right Wing Pool
                                </p>
                                <h3 className="text-2xl font-bold text-rose-500">
                                    {stats.right}{" "}
                                    <span className="text-base font-semibold text-rose-400">Members</span>
                                </h3>
                                <p className={`text-[11px] font-semibold flex items-center gap-1
                                    ${stats.balanced ? "text-teal-600" : "text-amber-500"}`}>
                                    <AlertTriangle className="w-3 h-3" />
                                    {stats.balanced ? "Well balanced" : "Growth opportunity"}
                                </p>
                            </div>
                            <div className="w-11 h-11 bg-rose-50 rounded-xl grid place-items-center text-rose-500 shrink-0">
                                <ArrowRight className="w-5 h-5" />
                            </div>
                        </div>

                        {/* Pairing Ratio */}
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm
                            flex items-center justify-between hover:shadow-md transition-shadow">
                            <div className="space-y-1">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                    Pairing Ratio
                                </p>
                                <h3 className="text-2xl font-bold text-slate-800">
                                    {stats.ratio}{" "}
                                    <span className="text-base font-semibold text-slate-500">Balance</span>
                                </h3>
                                <p className={`text-[11px] font-semibold flex items-center gap-1
                                    ${stats.balanced ? "text-teal-600" : "text-slate-500"}`}>
                                    <Crown className="w-3 h-3" />
                                    {stats.balanced ? "Sovereign active" : "Building balance"}
                                </p>
                            </div>
                            <div className="w-11 h-11 bg-purple-50 rounded-xl grid place-items-center text-purple-500 shrink-0">
                                <Scale className="w-5 h-5" />
                            </div>
                        </div>
                    </div>
                )}

                {/* ── Toolbar ── */}
                <div className="bg-white rounded-3xl shadow-sm overflow-hidden">

                    {/* Row 1 — always visible */}
                    <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">

                        {/* Left: explorer + depth + member selector */}
                        <div className="flex items-center gap-2.5 flex-wrap">
                            {/* Explorer ping label */}
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-xl">
                                <span className="relative flex h-2 w-2 shrink-0">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-teal-500" />
                                </span>
                                <span className="text-xs text-slate-500">
                                    {isOwn
                                        ? <><span className="font-semibold text-slate-700">Explorer</span>: {user?.full_name}'s full matrix</>
                                        : selectedMember
                                            ? <>
                                                <span className="font-semibold text-slate-700">Viewing</span>: {selectedMember.full_name}'s sub-tree
                                                <button
                                                    onClick={() => setSelectedId(user?.id)}
                                                    className="ml-1.5 text-slate-400 hover:text-slate-600 inline-flex align-middle">
                                                    <X className="w-3 h-3" />
                                                </button>
                                              </>
                                            : "Loading…"
                                    }
                                </span>
                            </div>

                            {/* Depth selector */}
                            <div className="relative">
                                <select
                                    value={depth}
                                    onChange={e => setDepth(Number(e.target.value))}
                                    className="appearance-none bg-slate-50 border border-slate-100 pl-3.5 pr-8 py-2
                                        rounded-xl text-xs font-semibold text-slate-700 outline-none cursor-pointer
                                        focus:ring-2 focus:ring-[#2172E5]/20"
                                >
                                    {[3, 5, 7, 10, 12, 15, 20].map(n => (
                                        <option key={n} value={n}>Depth: {n} levels</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-2.5 top-2.5 w-3 h-3 text-slate-400 pointer-events-none" />
                            </div>

                            {/* Member selector — all team (member) or all system members (admin) */}
                            {teamMembers.length > 0 && (
                                <div className="relative">
                                    <select
                                        value={selectedId || ""}
                                        onChange={e => setSelectedId(e.target.value)}
                                        className="appearance-none bg-slate-50 border border-slate-100 pl-3 pr-8 py-2
                                            rounded-xl text-xs font-semibold text-slate-700 outline-none cursor-pointer
                                            focus:ring-2 focus:ring-[#2172E5]/20 max-w-[220px]"
                                    >
                                        <option value={user?.id}>
                                            {isAdmin ? "Platform Root View" : `My Tree — ${user?.full_name}`}
                                        </option>
                                        {leftMembers.length > 0 && (
                                            <optgroup label={`◄ Left Wing (${leftMembers.length})`}>
                                                {leftMembers.map(m => (
                                                    <option key={m.id} value={m.id}>
                                                        {m.full_name} ({m.referral_code})
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                        {rightMembers.length > 0 && (
                                            <optgroup label={`► Right Wing (${rightMembers.length})`}>
                                                {rightMembers.map(m => (
                                                    <option key={m.id} value={m.id}>
                                                        {m.full_name} ({m.referral_code})
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                        {/* Members with no side (e.g. not yet placed) */}
                                        {teamMembers.filter(m => !m.side).length > 0 && (
                                            <optgroup label="— Unplaced">
                                                {teamMembers.filter(m => !m.side).map(m => (
                                                    <option key={m.id} value={m.id}>
                                                        {m.full_name} ({m.referral_code})
                                                    </option>
                                                ))}
                                            </optgroup>
                                        )}
                                    </select>
                                    <ChevronDown className="absolute right-2.5 top-2.5 w-3 h-3 text-slate-400 pointer-events-none" />
                                </div>
                            )}
                        </div>{/* end left group */}

                        {/* Right: view toggle + register */}
                        <div className="flex items-center gap-2.5 shrink-0">
                            {/* View mode toggle */}
                            <div className="flex bg-slate-100 p-1 rounded-xl gap-0.5">
                                <button
                                    onClick={() => { setViewMode("tree"); setWingFilter("ALL"); }}
                                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all
                                        ${viewMode === "tree"
                                            ? "bg-white text-slate-800 shadow-sm"
                                            : "text-slate-500 hover:text-slate-700"
                                        }`}
                                >
                                    <GitBranch className="w-3.5 h-3.5" /> Tree View
                                </button>
                                <button
                                    onClick={() => setViewMode("table")}
                                    className={`flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold rounded-lg transition-all
                                        ${viewMode === "table"
                                            ? "bg-white text-slate-800 shadow-sm"
                                            : "text-slate-500 hover:text-slate-700"
                                        }`}
                                >
                                    <Table2 className="w-3.5 h-3.5" /> Table View
                                </button>
                            </div>

                            {/* Register Node */}
                            <button
                                onClick={() => navigate("/app/add-member")}
                                className="flex items-center gap-1.5 bg-gradient-to-r from-teal-500 to-emerald-500
                                    hover:from-teal-600 hover:to-emerald-600 text-white font-semibold text-xs
                                    px-4 py-2.5 rounded-xl transition-all shadow-sm shadow-emerald-500/20 active:scale-[0.98]"
                            >
                                <Plus className="w-3.5 h-3.5" /> Register Node
                            </button>
                        </div>{/* end right group */}
                    </div>{/* end Row 1 */}

                    {/* Row 2 — Wing filter pills: Table View only */}
                    {viewMode === "table" && (
                        <div className="px-4 pb-3 flex items-center gap-2 border-t border-slate-50">
                            <span className="text-[11px] font-semibold text-slate-400 shrink-0 mr-1">
                                Filter by wing:
                            </span>
                            <div className="flex bg-slate-100 p-1 rounded-xl gap-0.5">
                                {WING_PILLS.map(({ label, value }) => (
                                    <button
                                        key={value}
                                        onClick={() => setWingFilter(value)}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all
                                            ${wingFilter === value
                                                ? "bg-white text-slate-800 shadow-sm"
                                                : "text-slate-500 hover:text-slate-700"
                                            }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>{/* end Toolbar */}

                {/* ── Content panel ── */}
                <div className={`rounded-3xl shadow-sm overflow-hidden min-h-[520px] relative
                    ${viewMode === "tree"
                        ? "bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:16px_16px]"
                        : "bg-white"
                    }`}>
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-24 gap-4">
                            <Network className="w-12 h-12 animate-pulse text-[#2172E5]/40" />
                            <span className="text-sm font-medium text-slate-400">Loading tree…</span>
                        </div>
                    ) : root ? (
                        viewMode === "tree"
                            ? <TreeView root={root} />
                            : <TreeTable root={root} wingFilter={wingFilter} search={search} />
                    ) : (
                        <div className="flex flex-col items-center justify-center py-24 gap-3">
                            <Network className="w-12 h-12 text-slate-200" />
                            <span className="text-sm text-slate-400">No tree data available.</span>
                        </div>
                    )}
                </div>

            </div>
        </AppShell>
    );
}
